import { X } from 'lucide-react'
import { StepRow } from '@/components/StepRow'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_HEADING, FONT_BODY, BRAND_GRADIENT } from '@/config/theme'
import type { ProgressStep } from '@/utils/steps'
import type { Chain } from '@/types'

interface ProgressPanelProps {
  title: string
  amount: string
  sourceToken: string
  destToken: string
  sourceChain: Chain | null
  destChain: Chain | null
  steps: ProgressStep[]
  isComplete: boolean
  isError: boolean
  /**
   * DEV-375 — the transfer was BROADCAST and no receipt could be obtained.
   *
   * A THIRD terminal mode, mutually exclusive with `isComplete` and `isError` at every mount. It
   * must never be expressed as `isError` with softer copy: `isError` drives the red header
   * (`headerColor` below) AND the error footer, whose button resets the form — the two things a
   * hash-in-flight outcome may not do (I1, I3). Callers therefore pass `isError={false}` and this
   * instead; the panel renders a neutral header and a single "Close" that only dismisses.
   */
  isUnknown?: boolean
  onDismiss: () => void
  onStartNew: () => void
  onRetry?: () => void
  /**
   * #261 F9 (I-3) — the label for the error footer's `onStartNew` button. REQUIRED, and supplied per
   * mount, because `onStartNew` does something different at each one: on the panel opened from
   * Activity it only closes the panel ("Close"), while on the two live panels it really resets the
   * form ("Start over"). A single hardcoded literal is what made this button read "Cancel" while
   * cancelling nothing — the real cancel flow lives in the Activity drawer and never reached here.
   */
  startNewLabel: string
  /**
   * #261 F9 (I-3) — the same thing for the SUCCESS footer's button, which fires the SAME `onStartNew`.
   * It is a separate prop, not a reuse of `startNewLabel`, only because the two footers had different
   * pre-F9 copy ("Cancel" vs "Start new transfer") and flag-off rendering must stay byte-identical to
   * each (F-C); the two are never on screen together (`isComplete` and `isError` are mutually
   * exclusive at every mount).
   *
   * This branch had the SAME defect as the "Cancel" one and was one `if` away from it: the label was
   * hardcoded "Start new transfer", so the drawer mount — whose `onStartNew` is
   * `() => setProgressTransfer(null)`, i.e. close and nothing else — offered to start a transfer it
   * cannot start. Fixing the error footer alone would have left the violation live on the success path.
   */
  completeLabel: string
}

export function ProgressPanel({
  title,
  amount,
  sourceToken,
  destToken,
  sourceChain,
  destChain,
  steps,
  isComplete,
  isError,
  isUnknown = false,
  onDismiss,
  onStartNew,
  onRetry,
  startNewLabel,
  completeLabel,
}: ProgressPanelProps) {
  const c = useThemeColors()

  // DEV-375: the unknown tone is the theme's warning colour — explicitly NOT `c.eC`. `isError` is
  // still checked first so a caller that (wrongly) set both would still get the louder, safer red.
  const headerColor = isComplete ? c.gr : isError ? c.eC : isUnknown ? c.wC : c.ac

  return (
    <div style={{
      marginTop: 24, width: '100%', maxWidth: 480,
      background: c.card, border: `1px solid ${c.cb}`,
      borderRadius: 20, padding: '20px 20px 0',
      boxShadow: c.sh, fontFamily: FONT_BODY,
      animation: 'slideUp 0.3s ease-out',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <div>
          <div style={{
            fontSize: 16, fontWeight: 600, fontFamily: FONT_HEADING,
            color: headerColor,
          }}>
            {title}
          </div>
          <div style={{ fontSize: 13, color: c.mu, marginTop: 2 }}>
            {amount} {sourceToken}
            {sourceToken !== destToken && ` → ${destToken}`}
            {sourceChain && destChain && sourceChain !== destChain && (
              <span style={{ textTransform: 'capitalize' }}>
                {' '}({sourceChain} → {destChain})
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onDismiss}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: c.mu, padding: 4, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          aria-label="Dismiss"
        >
          <X size={18} />
        </button>
      </div>

      {/* Steps */}
      <div style={{ marginBottom: 8, padding: '0 0 16px' }}>
        {steps.map((step, i) => (
          <StepRow
            key={step.id}
            step={step}
            isLast={i === steps.length - 1}
            sourceChain={sourceChain}
            destChain={destChain}
          />
        ))}
      </div>

      {/* Success footer */}
      {isComplete && (
        <div style={{
          padding: '14px 16px',
          background: c.oB + '55',
          display: 'flex', justifyContent: 'center',
        }}>
          <button
            onClick={onStartNew}
            style={{
              background: BRAND_GRADIENT,
              border: 'none',
              borderRadius: 12,
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: FONT_BODY,
              color: '#fff',
              cursor: 'pointer',
              textShadow: '0 1px 2px rgba(0,50,80,0.35)',
            }}
          >
            {completeLabel}
          </button>
        </div>
      )}

      {/* DEV-375 unknown footer. ONE control, and it is `onDismiss` — the same thing the X does.
          No retry (a resubmit of a value transfer whose fate is unknown is the double-spend this
          ticket exists to prevent) and no reset (`onStartNew` clears the form on both live mounts,
          which is how the user loses the only on-screen reference to their in-flight hash). The
          label is hardcoded rather than passed per mount — unlike `startNewLabel`, whose whole
          point is that `onStartNew` differs per mount — because this button fires `onDismiss`
          everywhere, and "Close" is the accurate word for it at every mount. */}
      {isUnknown && !isComplete && !isError && (
        <div style={{
          padding: '14px 16px',
          background: c.wB + '55',
          display: 'flex', justifyContent: 'center',
        }}>
          <button
            onClick={onDismiss}
            style={{
              background: 'transparent',
              border: `1px solid ${c.dv}`,
              borderRadius: 12,
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: FONT_BODY,
              color: c.tx,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      )}

      {/* Error footer */}
      {isError && (
        <div style={{
          padding: '14px 16px',
          background: c.eB + '55',
          display: 'flex', justifyContent: 'center', gap: 10,
        }}>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                background: c.eC,
                border: 'none',
                borderRadius: 12,
                padding: '10px 24px',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: FONT_BODY,
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          )}
          <button
            onClick={onStartNew}
            style={{
              background: 'transparent',
              border: `1px solid ${c.dv}`,
              borderRadius: 12,
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: FONT_BODY,
              color: c.tx,
              cursor: 'pointer',
            }}
          >
            {startNewLabel}
          </button>
        </div>
      )}
    </div>
  )
}
